import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DropdownIcon } from "../../asserts/logo/dropdown.svg";
import { TRUST_ATTRIBUTES, trustAttributeLabel } from "../../config/trustAttributes";

const ATTRIBUTES = TRUST_ATTRIBUTES;
const SEVERITIES = ["Critical", "High", "Medium", "Low"];
const LIKELIHOODS = ["High", "Medium", "Low"];
const OWNERS = [
  "Responsible AI lead",
  "Risk manager",
  "Security governance lead",
  "Privacy officer",
  "QA Lead",
];

const EMPTY = {
  name: "",
  description: "",
  attribute: "Reliability",
  severity: "High",
  likelihood: "Medium",
  owner: "Responsible AI lead",
  reference: "",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#fff",
    fontSize: "16px",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#98A2B3" },
    "&.Mui-focused fieldset": { borderColor: "#555555", borderWidth: 1 },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#707070",
    opacity: 1,
  },
};

function RequiredMark() {
  return (
    <Typography component="span" sx={{ color: "#DE350B", ml: 0.25, fontWeight: 700 }}>
      *
    </Typography>
  );
}

function FieldLabel({ children, required }) {
  return (
    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#707070", mb: 0.75 }}>
      {required ? <RequiredMark /> : null}
      {children}
    </Typography>
  );
}

function HelperText({ children }) {
  return (
    <Typography sx={{ fontSize: "12px", color: "#707070", mt: 0.75, lineHeight: "18px" }}>
      {children}
    </Typography>
  );
}

export default function AddRiskDialog({ open, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      setSubmitting(true);
      const description = form.description.trim();
      const reference = form.reference.trim();
      await onSave({
        name: form.name.trim(),
        description: reference
          ? `${description}${description ? "\n\n" : ""}Reference: ${reference}`
          : description,
        attribute: form.attribute,
        severity: form.severity,
        likelihood: form.likelihood,
        owner: form.owner,
        source: "Defined",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100vw",
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #EAECF0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#555555", lineHeight: "24px" }}>
              Add enterprise risk
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{ color: "#555555", mt: -0.5, mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: "14px", color: "#555555", mt: 1, lineHeight: "24px", pr: 2 }}>
            Spotted something specific to your business that the canonical register doesn&apos;t cover? Add it here.
          </Typography>
        </Box>

        {/* Form body */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
          <Stack spacing={2.5}>
            <Box>
              <FieldLabel required>Risk name</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. Underwriting bias across protected classes"
                value={form.name}
                onChange={handle("name")}
                sx={fieldSx}
              />
              <HelperText>
                Short, action-oriented. This is how the risk appears in dashboards and reports.
              </HelperText>
            </Box>

            <Box>
              <FieldLabel>Description</FieldLabel>
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Describe the failure mode, who is affected, and why it matters for this AI."
                value={form.description}
                onChange={handle("description")}
                sx={fieldSx}
              />
              <HelperText>what could go wrong, and to whom</HelperText>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Box>
                <FieldLabel required>Trust attribute</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon}
                    value={form.attribute}
                    onChange={handle("attribute")}
                    displayEmpty
                    sx={{
                      borderRadius: "4px",
                      fontSize: "16px"	,
                      color: "#707070",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#555555" },
                    }}
                  >
                    {ATTRIBUTES.map((a) => (
                      <MenuItem key={a} value={a}>{trustAttributeLabel(a)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <FieldLabel required>Owner</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon}
                    value={form.owner}
                    onChange={handle("owner")}
                    displayEmpty
                    sx={{
                      borderRadius: "4px",
                      fontSize: "16px",
                      color: "#707070",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#555555" },
                    }}
                  >
                    {OWNERS.map((o) => (
                      <MenuItem key={o} value={o}>{o}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <FieldLabel required>Severity</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon}
                    value={form.severity}
                    onChange={handle("severity")}
                    displayEmpty
                    sx={{
                      borderRadius: "4px",
                      fontSize: "16px",
                      color: "#707070",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#555555" },
                    }}
                  >
                    {SEVERITIES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <FieldLabel required>Likelihood</FieldLabel>
                <FormControl fullWidth size="small">
                  <Select IconComponent={DropdownIcon}
                    value={form.likelihood}
                    onChange={handle("likelihood")}
                    displayEmpty
                    sx={{
                      borderRadius: "4px",
                      fontSize: "16px",
                      color: "#707070",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#555555" },
                    }}
                  >
                    {LIKELIHOODS.map((l) => (
                      <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <FieldLabel>Reference / Source</FieldLabel>
              <TextField
                fullWidth
                size="small"
                placeholder="https://... or internal doc ID"
                value={form.reference}
                onChange={handle("reference")}
                sx={fieldSx}
              />
              <HelperText>link to policy, regulation or incident</HelperText>
            </Box>
          </Stack>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #EAECF0",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            bgcolor: "#fff",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#667085", lineHeight: 1.4, flex: 1 }}>
            Derived risk — you can edit or delete it any time
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#005BF0",
                borderColor: "#1A6EF5",
                borderRadius: "4px",
                px: 2,
                "&:hover": { borderColor: "#1251c0", bgcolor: "rgba(26, 110, 245, 0.04)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={submitting || !form.name.trim()}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#005BF0",
                borderRadius: "4px",
                px: 2,
                boxShadow: "none",
                "&:hover": { bgcolor: "#1251c0", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#005BF0", color: "#fff" },
              }}
            >
              Save &amp; add risk
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
